import type { ComponentType } from 'react';
import { NoLogoIcon } from '@/components/Icon/ForceIcons';
import { ProductLogoSmallAccesses } from '@/components/Logo/Product/ProductLogoSmallAccesses';
import { ProductLogoSmallApps } from '@/components/Logo/Product/ProductLogoSmallApps';
import { ProductLogoSmallAttendanceCore } from '@/components/Logo/Product/ProductLogoSmallAttendanceCore';
import { ProductLogoSmallAttendanceEngine } from '@/components/Logo/Product/ProductLogoSmallAttendanceEngine';
import { ProductLogoSmallBusinessOperationEvents } from '@/components/Logo/Product/ProductLogoSmallBusinessOperationEvents';
import { ProductLogoSmallCertifications } from '@/components/Logo/Product/ProductLogoSmallCertifications';
import { ProductLogoSmallCockpit } from '@/components/Logo/Product/ProductLogoSmallCockpit';
import { ProductLogoSmallContractorCore } from '@/components/Logo/Product/ProductLogoSmallContractorCore';
import { ProductLogoSmallContracts } from '@/components/Logo/Product/ProductLogoSmallContracts';
import { ProductLogoSmallCustomer } from '@/components/Logo/Product/ProductLogoSmallCustomer';
import { ProductLogoSmallDevices } from '@/components/Logo/Product/ProductLogoSmallDevices';
import { ProductLogoSmallEquipment } from '@/components/Logo/Product/ProductLogoSmallEquipment';
import { ProductLogoSmallFleet } from '@/components/Logo/Product/ProductLogoSmallFleet';
import { ProductLogoSmallGAOperationCore } from '@/components/Logo/Product/ProductLogoSmallGAOperationCore';
import { ProductLogoSmallIDs } from '@/components/Logo/Product/ProductLogoSmallIDs';
import { ProductLogoSmallITOperationCore } from '@/components/Logo/Product/ProductLogoSmallITOperationCore';
import { ProductLogoSmallLaborCore } from '@/components/Logo/Product/ProductLogoSmallLaborCore';
import { ProductLogoSmallLicenses } from '@/components/Logo/Product/ProductLogoSmallLicenses';
import { ProductLogoSmallMarketplace } from '@/components/Logo/Product/ProductLogoSmallMarketplace';
import { ProductLogoSmallNetworks } from '@/components/Logo/Product/ProductLogoSmallNetworks';
import { ProductLogoSmallOrganization } from '@/components/Logo/Product/ProductLogoSmallOrganization';
import { ProductLogoSmallPayroll } from '@/components/Logo/Product/ProductLogoSmallPayroll';
import { ProductLogoSmallPayrollCore } from '@/components/Logo/Product/ProductLogoSmallPayrollCore';
import { ProductLogoSmallPayrollEngine } from '@/components/Logo/Product/ProductLogoSmallPayrollEngine';
import { ProductLogoSmallPeople } from '@/components/Logo/Product/ProductLogoSmallPeople';
import { ProductLogoSmallPermissionAndRoles } from '@/components/Logo/Product/ProductLogoSmallPermissionAndRoles';
import { ProductLogoSmallPlaces } from '@/components/Logo/Product/ProductLogoSmallPlaces';
import { ProductLogoSmallPublicIDs } from '@/components/Logo/Product/ProductLogoSmallPublicIDs';
import { ProductLogoSmallSettings } from '@/components/Logo/Product/ProductLogoSmallSettings';
import { ProductLogoSmallSoftware } from '@/components/Logo/Product/ProductLogoSmallSoftware';
import { ProductLogoSmallStarlinks } from '@/components/Logo/Product/ProductLogoSmallStarlinks';
import { ProductLogoSmallSupplier } from '@/components/Logo/Product/ProductLogoSmallSupplier';
import { ProductLogoSmallTimeAndAttendance } from '@/components/Logo/Product/ProductLogoSmallTimeAndAttendance';
import { ProductLogoSmallUserAccounts } from '@/components/Logo/Product/ProductLogoSmallUserAccounts';
export type ProductLogoComponent = ComponentType<{
  className?: string;
}>;

/**
 * Staff routeTree の第1 path segment → ProductLogoSmall。
 * `ids` は pathToPascalCase だと `Ids` になるため、routeList の specialCase と同様に IDs を明示。
 */
const productLogoSegmentMap = {
  accesses: ProductLogoSmallAccesses,
  apps: ProductLogoSmallApps,
  'attendance-core': ProductLogoSmallAttendanceCore,
  'attendance-engine': ProductLogoSmallAttendanceEngine,
  'business-operation-events': ProductLogoSmallBusinessOperationEvents,
  certification: ProductLogoSmallCertifications,
  cockpit: ProductLogoSmallCockpit,
  contract: ProductLogoSmallContracts,
  'contractor-core': ProductLogoSmallContractorCore,
  customer: ProductLogoSmallCustomer,
  devices: ProductLogoSmallDevices,
  equipment: ProductLogoSmallEquipment,
  fleets: ProductLogoSmallFleet,
  'ga-operation-core': ProductLogoSmallGAOperationCore,
  ids: ProductLogoSmallIDs,
  'it-operation-core': ProductLogoSmallITOperationCore,
  'labor-core': ProductLogoSmallLaborCore,
  licenses: ProductLogoSmallLicenses,
  marketplace: ProductLogoSmallMarketplace,
  networks: ProductLogoSmallNetworks,
  organization: ProductLogoSmallOrganization,
  payroll: ProductLogoSmallPayroll,
  'payroll-core-mock': ProductLogoSmallPayrollCore,
  'payroll-engine-mock': ProductLogoSmallPayrollEngine,
  people: ProductLogoSmallPeople,
  places: ProductLogoSmallPlaces,
  'public-ids': ProductLogoSmallPublicIDs,
  settings: ProductLogoSmallSettings,
  software: ProductLogoSmallSoftware,
  starlinks: ProductLogoSmallStarlinks,
  supplier: ProductLogoSmallSupplier,
  'time-and-attendance': ProductLogoSmallTimeAndAttendance
} as const satisfies Record<string, ProductLogoComponent>;

/** routeList.ts specialCaseLogoMap と同一。より具体的な prefix を先に評価すること。 */
const productLogoPrefixMap = {
  '/settings/permission-and-roles': ProductLogoSmallPermissionAndRoles,
  '/settings/persons': ProductLogoSmallUserAccounts,
  '/ids': ProductLogoSmallIDs
} as const satisfies Record<string, ProductLogoComponent>;
const productLogoPrefixKeys = Object.keys(productLogoPrefixMap) as (keyof typeof productLogoPrefixMap)[];

/** Command Palette 用: staff ルート path から ProductLogoSmall（または NoLogoIcon）を解決する。 */
export function resolveProductLogoForPath(path: string): ProductLogoComponent {
  const matchingPrefix = productLogoPrefixKeys.find(prefix => path.startsWith(prefix));
  if (matchingPrefix) {
    return productLogoPrefixMap[matchingPrefix];
  }
  const pathSegment = path.split('/').filter(Boolean)[0];
  if (pathSegment && pathSegment in productLogoSegmentMap) {
    return productLogoSegmentMap[pathSegment as keyof typeof productLogoSegmentMap];
  }
  return NoLogoIcon;
}